import { useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { getLastActivityDate } from '../../activeLoanModel';
import type { ProspectLoan } from '../../types';

interface ConversationLogHoverCardProps {
  loan: Pick<ProspectLoan, 'conversation_logs' | 'last_conversation_log_at' | 'milestone_date'>;
  /**
   * Drives header copy + accent: amber alert for stalled and muted "Active"
   * for the activity chip.
   */
  variant: 'stalled' | 'active';
  /** Day count for the header (stalled: daysStale; active: daysSinceLastActivity). */
  days: number | null;
  /** The pill/chip rendered as the trigger. */
  children: ReactNode;
  className?: string;
}

function parseEntryDate(value: string | null): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Hover surface showing the loan's synced Encompass conversation log.
 *
 * Controlled HoverCard: opens on hover (with delay) and keyboard focus,
 * toggles on click/tap (touch support), Esc closes. The trigger is a real
 * button with reset styling so the wrapped pill keeps its exact geometry.
 * Click/pointer/keyboard activation stops propagation here — every direction
 * (Table row, Hybrid row, Pipeline card) opens loan details from container
 * clicks, and interacting with the badge must only toggle this card.
 */
export function ConversationLogHoverCard({
  loan,
  variant,
  days,
  children,
  className
}: ConversationLogHoverCardProps) {
  const [open, setOpen] = useState(false);

  const entries = loan.conversation_logs ?? [];
  // Same basis as the chip's day count (latest of milestone change OR newest
  // conversation entry) — a newer milestone must not show an older note date.
  const lastActivity = getLastActivityDate(loan);

  const dayPhrase = days === null ? '' : ` ${days} day${days === 1 ? '' : 's'}`;
  const ariaLabel = variant === 'stalled'
    ? `Stalled${dayPhrase} — view conversation log`
    : `Last activity${dayPhrase ? `${dayPhrase} ago` : ''} — view conversation log`;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen(previous => !previous);
  };
  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          className={cn(
            'appearance-none border-0 bg-transparent p-0 m-0 inline-flex cursor-pointer rounded-sm',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            className
          )}
        >
          {children}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={6}
        className="w-[40rem] max-w-[calc(100vw-2rem)] p-0"
        onClick={event => event.stopPropagation()}
      >
        <div className="px-3 py-2 border-b border-border">
          <p
            className={cn(
              'text-[10px] font-medium uppercase tracking-wider',
              variant === 'stalled' ? 'text-warning' : 'text-muted-foreground'
            )}
          >
            {variant === 'stalled'
              ? `Stalled${days === null ? '' : ` · ${days}d`}`
              : `Active${days === null ? '' : ` · ${days}d`}`}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {variant === 'stalled'
              ? days === null ? 'No recent activity' : `No activity for ${days} day${days === 1 ? '' : 's'}`
              : lastActivity
                ? `Last activity ${format(lastActivity, 'MMM d, yyyy')}`
                : 'No recent activity recorded'}
          </p>
        </div>

        {entries.length === 0 ? (
          <p className="px-3 py-4 text-[11px] text-muted-foreground">
            No conversation log entries synced.
          </p>
        ) : (
          // Native scroll, NOT Radix ScrollArea: the Radix viewport needs a
          // definite height — with only max-h it clips overflow unscrollably.
          <div
            data-testid="conversation-log-scroll"
            tabIndex={0}
            className="max-h-64 overflow-y-auto overscroll-contain focus-visible:outline-none"
          >
            <div className="px-3 py-1">
              {entries.map((entry, index) => {
                const author = entry.authorName ?? entry.authorId ?? 'Unknown';
                const createdAt = parseEntryDate(entry.createdDate);
                const showTitle = !!entry.name && entry.name !== author;
                return (
                  <div
                    key={entry.id ?? `${entry.createdDate ?? 'entry'}-${index}`}
                    className={cn('py-2', index > 0 && 'border-t border-dashed border-border/60')}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[11px] font-medium text-foreground truncate">{author}</span>
                      {createdAt && (
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {format(createdAt, 'MMM d, yyyy')} · {formatDistanceToNow(createdAt, { addSuffix: true })}
                        </span>
                      )}
                    </div>
                    {showTitle && (
                      <p className="text-[10px] text-muted-foreground">{entry.name}</p>
                    )}
                    {entry.comments && (
                      <p className="mt-0.5 text-[11px] leading-snug text-foreground/90 whitespace-pre-wrap break-words">
                        {entry.comments}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground">
          Conversation log · via Encompass
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
